import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError } from '../notify';

// Machine tokens for the phone fleet's bridge (backend routes/fleetTokens.js).
// A token acts for one organisation, only on the executor endpoints, and only
// for accounts bound to the fleet. Shown once at creation.
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const day = (t) => (t ? new Date(t).toLocaleDateString() : '—');

function FleetTokens({ role, organizations }) {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [shown, setShown] = useState(null); // { name, token } just created
  const [busy, setBusy] = useState(false);
  const superAdmin = role === 'super_admin';

  const load = async () => {
    try { setTokens((await axios.get(`${API}/api/fleet-tokens`, auth())).data || []); }
    catch (err) { notifyError(err, 'Could not load fleet tokens'); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    if (superAdmin && !orgId) { alert('Pick the organisation the token acts for'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/fleet-tokens`, { name: name.trim(), organization_id: orgId || undefined }, auth());
      setShown({ name: res.data.row.name, token: res.data.token });
      setName('');
      load();
    } catch (err) { notifyError(err, 'Could not create the token'); }
    setBusy(false);
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.name}"? The fleet stops working with it at once.`)) return;
    try { await axios.post(`${API}/api/fleet-tokens/${t.id}/revoke`, {}, auth()); load(); }
    catch (err) { notifyError(err, 'Could not revoke the token'); }
  };

  const orgName = (id) => organizations.find(o => o.id === id)?.name || 'Unknown org';

  return (
    <div className="card">
      <h2>Fleet tokens</h2>
      <p className="sub">For the phone fleet's bridge. A token acts for one organisation and only runs accounts bound to the fleet.</p>

      {shown && (
        <div style={{ background: '#0f1f0f', border: '1px solid #22c55e', borderRadius: '8px', padding: '12px', margin: '12px 0' }}>
          <div style={{ fontSize: '13px', marginBottom: '8px' }}>
            Token for <strong>{shown.name}</strong>. Copy it now: it is not shown again.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input readOnly value={shown.token} style={{ flex: 1, fontFamily: 'monospace' }} onFocus={e => e.target.select()} />
            <button onClick={() => navigator.clipboard.writeText(shown.token).catch(() => {})}>Copy</button>
            <button className="btn-secondary" onClick={() => setShown(null)}>Done</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
        <input placeholder="Token name (e.g. rice-media fleet bridge)" value={name} onChange={e => setName(e.target.value)} style={{ flex: 1 }}
          onKeyDown={e => { if (e.key === 'Enter') create(); }} />
        {superAdmin && (
          <select value={orgId} onChange={e => setOrgId(e.target.value)}>
            <option value="">— Organisation —</option>
            {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
        <button onClick={create} disabled={busy || !name.trim()}>{busy ? 'Creating...' : '+ Create token'}</button>
      </div>

      {tokens.length === 0 ? <p className="empty">No fleet tokens yet.</p> : (
        <div className="list">
          {tokens.map(t => (
            <div className="list-item" key={t.id}>
              <div>
                <strong style={{ opacity: t.revoked_at ? 0.5 : 1 }}>{t.name}</strong>
                {superAdmin && <span style={{ marginLeft: '10px', fontSize: '10px', color: '#555', background: '#1a1a1a', padding: '2px 6px', borderRadius: '6px' }}>{orgName(t.organization_id)}</span>}
                {t.revoked_at && <span style={{ marginLeft: '10px', fontSize: '10px', color: '#ef4444', background: '#2a0a0a', padding: '2px 6px', borderRadius: '6px' }}>REVOKED {day(t.revoked_at)}</span>}
                <span className="sub" style={{ marginLeft: '12px' }}>
                  Created {day(t.created_at)}{t.created_by_name ? ` by ${t.created_by_name}` : ''} · last used {day(t.last_used_at)}
                </span>
              </div>
              {!t.revoked_at && (
                <div className="list-item-actions">
                  <button className="btn-danger" onClick={() => revoke(t)}>Revoke</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FleetTokens;
